/**
 * Plan de Cuentas - Finance 360
 * Tree view and management of chart of accounts
 */

import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  BookOpen,
  Plus,
  Search,
  ChevronRight,
  ChevronDown,
  Edit2,
  Trash2,
  Building2,
  X,
  Check,
  AlertCircle,
  RefreshCw,
  FolderTree
} from 'lucide-react';
import { contabilidadApi } from '../../utils/api';
import useAuthStore from '../../store/authStore';
import LoadingSpinner from '../../components/LoadingSpinner';
import ConfirmModal from '../../components/ConfirmModal';
import useToolbarResponsive from '../../hooks/useToolbarResponsive';

// Toolbar button component
function ToolbarButton({ icon: Icon, label, onClick, disabled, variant = 'default', badge }) {
  const baseClasses = "flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px]";
  const variants = {
    default: "border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400",
    primary: "border border-blue-400 text-blue-600 hover:bg-blue-50 hover:border-blue-500",
    success: "border border-green-400 text-green-600 hover:bg-green-50 hover:border-green-500",
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variants[variant]} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} relative`}
    >
      <Icon className="h-5 w-5 mb-1" />
      <span className="whitespace-nowrap">{label}</span>
      {badge && (
        <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
          {badge}
        </span>
      )}
    </button>
  );
}

function ToolbarSeparator() {
  return <div className="w-px h-12 bg-gray-300 mx-1" />;
}

// Account type colors
const tipoColors = {
  activo: 'bg-blue-100 text-blue-700',
  pasivo: 'bg-red-100 text-red-700',
  patrimonio: 'bg-green-100 text-green-700',
  ingreso: 'bg-purple-100 text-purple-700',
  gasto: 'bg-orange-100 text-orange-700',
  costo: 'bg-yellow-100 text-yellow-700'
};

// Tree node component
function CuentaNode({ cuenta, level = 0, onEdit, onDelete, expandedIds, toggleExpand }) {
  const hasChildren = cuenta.children && cuenta.children.length > 0;
  const isExpanded = expandedIds.has(cuenta.id);

  return (
    <div>
      <div
        className={`flex items-center gap-2 py-2 px-3 hover:bg-gray-50 rounded-lg group ${
          level > 0 ? 'ml-6' : ''
        }`}
      >
        {/* Expand button */}
        <button
          onClick={() => toggleExpand(cuenta.id)}
          className={`w-5 h-5 flex items-center justify-center ${hasChildren ? '' : 'invisible'}`}
        >
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-500" />
          )}
        </button>

        {/* Code */}
        <span className="font-mono text-sm text-gray-600 w-24">{cuenta.codigo}</span>

        {/* Name */}
        <span className={`flex-1 text-sm ${cuenta.esCuentaMovimiento ? 'font-medium' : 'text-gray-600'}`}>
          {cuenta.nombre}
        </span>

        {/* Type badge */}
        <span className={`text-xs px-2 py-0.5 rounded-full ${tipoColors[cuenta.tipo]}`}>
          {cuenta.tipo}
        </span>

        {/* Movement indicator */}
        {cuenta.esCuentaMovimiento && (
          <span className="text-xs text-gray-400">Mov.</span>
        )}

        {/* Balance */}
        {cuenta.saldoActual !== undefined && cuenta.saldoActual !== 0 && (
          <span className={`text-sm font-medium ${
            cuenta.saldoActual >= 0 ? 'text-gray-900' : 'text-red-600'
          }`}>
            {new Intl.NumberFormat('es-CR', {
              style: 'currency',
              currency: 'CRC',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }).format(cuenta.saldoActual)}
          </span>
        )}

        {/* Actions */}
        {!cuenta.esCuentaSistema && (
          <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1">
            <button
              onClick={() => onEdit(cuenta)}
              className="p-1 text-gray-400 hover:text-blue-600"
            >
              <Edit2 className="h-4 w-4" />
            </button>
            {cuenta.movimientos === 0 && (
              <button
                onClick={() => onDelete(cuenta)}
                className="p-1 text-gray-400 hover:text-red-600"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div className="border-l border-gray-200 ml-5">
          {cuenta.children.map((child) => (
            <CuentaNode
              key={child.id}
              cuenta={child}
              level={level + 1}
              onEdit={onEdit}
              onDelete={onDelete}
              expandedIds={expandedIds}
              toggleExpand={toggleExpand}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Modal for creating/editing account
function CuentaModal({ cuenta, parentCuenta, onClose, onSave }) {
  const [form, setForm] = useState({
    codigo: cuenta?.codigo || (parentCuenta ? `${parentCuenta.codigo}.` : ''),
    nombre: cuenta?.nombre || '',
    descripcion: cuenta?.descripcion || '',
    tipo: cuenta?.tipo || parentCuenta?.tipo || 'gasto',
    naturaleza: cuenta?.naturaleza || (parentCuenta?.naturaleza || 'debito'),
    nivel: cuenta?.nivel || (parentCuenta ? parentCuenta.nivel + 1 : 1),
    cuentaPadreId: cuenta?.cuentaPadreId || parentCuenta?.id || null,
    esCuentaMovimiento: cuenta?.esCuentaMovimiento ?? true,
    codigoHacienda: cuenta?.codigoHacienda || '',
    saldoInicial: cuenta?.saldoInicial || 0
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      if (cuenta?.id) {
        await contabilidadApi.updateCuenta(cuenta.id, form);
      } else {
        await contabilidadApi.createCuenta(form);
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-lg p-6 m-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">
            {cuenta ? 'Editar Cuenta' : 'Nueva Cuenta'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Código
              </label>
              <input
                type="text"
                value={form.codigo}
                onChange={(e) => setForm({ ...form, codigo: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="1.1.01.001"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nivel
              </label>
              <select
                value={form.nivel}
                onChange={(e) => setForm({ ...form, nivel: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value={1}>1 - Clase</option>
                <option value={2}>2 - Grupo</option>
                <option value={3}>3 - Cuenta</option>
                <option value={4}>4 - Subcuenta</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre
            </label>
            <input
              type="text"
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Nombre de la cuenta"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo
              </label>
              <select
                value={form.tipo}
                onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="activo">Activo</option>
                <option value="pasivo">Pasivo</option>
                <option value="patrimonio">Patrimonio</option>
                <option value="ingreso">Ingreso</option>
                <option value="gasto">Gasto</option>
                <option value="costo">Costo</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Naturaleza
              </label>
              <select
                value={form.naturaleza}
                onChange={(e) => setForm({ ...form, naturaleza: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="debito">Débito</option>
                <option value="credito">Crédito</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descripción (opcional)
            </label>
            <textarea
              value={form.descripcion}
              onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Saldo Inicial
              </label>
              <input
                type="number"
                step="0.01"
                value={form.saldoInicial}
                onChange={(e) => setForm({ ...form, saldoInicial: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="0.00"
              />
              <p className="text-xs text-gray-500 mt-1">
                Para migración de datos o apertura
              </p>
            </div>
            <div className="flex items-center">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.esCuentaMovimiento}
                  onChange={(e) => setForm({ ...form, esCuentaMovimiento: e.target.checked })}
                  className="w-4 h-4 rounded text-blue-600"
                />
                <span className="text-sm text-gray-700">Cuenta de movimiento</span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? <LoadingSpinner size="sm" /> : <Check className="h-4 w-4" />}
              {cuenta ? 'Guardar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function PlanCuentas() {
  const { currentEmisor } = useAuthStore();
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const { searchRef, buttonsRef } = useToolbarResponsive([filterTipo]);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCuenta, setEditingCuenta] = useState(null);
  const [parentCuenta, setParentCuenta] = useState(null);
  const [seeding, setSeeding] = useState(false);

  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null, variant: 'warning' });

  const loadCuentas = useCallback(async () => {
    setLoading(true);
    try {
      const response = await contabilidadApi.getCuentas({ tipo: filterTipo || undefined });
      setCuentas(response.data.data);

      // Auto-expand first level
      const firstLevel = response.data.data.map(c => c.id);
      setExpandedIds(new Set(firstLevel));
    } catch (error) {
      console.error('Error loading cuentas:', error);
    } finally {
      setLoading(false);
    }
  }, [filterTipo]);

  useEffect(() => {
    if (currentEmisor) {
      loadCuentas();
    }
  }, [currentEmisor, loadCuentas]);

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleEdit = (cuenta) => {
    setEditingCuenta(cuenta);
    setParentCuenta(null);
    setModalOpen(true);
  };

  const handleAddChild = (parent) => {
    setEditingCuenta(null);
    setParentCuenta(parent);
    setModalOpen(true);
  };

  const handleDelete = (cuenta) => {
    setConfirmModal({
      isOpen: true,
      title: 'Eliminar Cuenta',
      message: `¿Está seguro de eliminar la cuenta ${cuenta.codigo} - ${cuenta.nombre}?`,
      variant: 'danger',
      onConfirm: async () => {
        try {
          await contabilidadApi.deleteCuenta(cuenta.id);
          toast.success('Cuenta eliminada correctamente');
          loadCuentas();
        } catch (error) {
          toast.error(error.response?.data?.error || 'Error al eliminar');
        }
      }
    });
  };

  const handleSeedPlan = () => {
    setConfirmModal({
      isOpen: true,
      title: 'Cargar Plan de Cuentas',
      message: 'Esto creará el plan de cuentas base NIIF para PYMES. ¿Desea continuar?',
      variant: 'info',
      onConfirm: async () => {
        setSeeding(true);
        try {
          const response = await contabilidadApi.seedPlanCuentas();
          toast.success(`Plan creado: ${response.data.data.cuentasCreadas} cuentas`);
          loadCuentas();
        } catch (error) {
          toast.error(error.response?.data?.error || 'Error al crear plan');
        } finally {
          setSeeding(false);
        }
      }
    });
  };

  // Filter cuentas by search
  const filterCuentas = (cuentas) => {
    if (!search) return cuentas;
    const lower = search.toLowerCase();

    const filterRecursive = (cuenta) => {
      const matches = cuenta.codigo.toLowerCase().includes(lower) ||
                     cuenta.nombre.toLowerCase().includes(lower);

      if (cuenta.children) {
        const filteredChildren = cuenta.children
          .map(filterRecursive)
          .filter(c => c !== null);

        if (filteredChildren.length > 0) {
          return { ...cuenta, children: filteredChildren };
        }
      }

      return matches ? cuenta : null;
    };

    return cuentas.map(filterRecursive).filter(c => c !== null);
  };

  const filteredCuentas = filterCuentas(cuentas);

  return (
    <div className="space-y-4">
      {/* Toolbar — buscador/filtros absolute izq, botones centrados (responsive con hook) */}
      <div className="bg-white rounded-lg border p-3 relative">
        <div ref={searchRef} className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-2 z-10">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por código o nombre..."
              className="pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 w-64"
            />
          </div>

          <select
            value={filterTipo}
            onChange={(e) => setFilterTipo(e.target.value)}
            className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
          >
            <option value="">Todos los tipos</option>
            <option value="activo">Activo</option>
            <option value="pasivo">Pasivo</option>
            <option value="patrimonio">Patrimonio</option>
            <option value="ingreso">Ingreso</option>
            <option value="gasto">Gasto</option>
            <option value="costo">Costo</option>
          </select>
        </div>

        <div ref={buttonsRef} className="flex items-center justify-center gap-2 flex-wrap">
          <ToolbarButton
            icon={Plus}
            label="Nueva Cuenta"
            onClick={() => { setEditingCuenta(null); setParentCuenta(null); setModalOpen(true); }}
            variant="primary"
          />

          {cuentas.length === 0 && (
            <ToolbarButton
              icon={Building2}
              label="Cargar Base"
              onClick={handleSeedPlan}
              disabled={seeding}
              variant="success"
            />
          )}

          <ToolbarButton
            icon={RefreshCw}
            label="Actualizar"
            onClick={loadCuentas}
            disabled={loading}
          />
        </div>
      </div>

      {/* Tree View */}
      <div className="bg-white rounded-xl border">
        {loading ? (
          <div className="p-8 flex justify-center">
            <LoadingSpinner />
          </div>
        ) : filteredCuentas.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {cuentas.length === 0 ? (
              <div>
                <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                <p>No hay cuentas configuradas</p>
                <p className="text-sm">Cargue el plan base para comenzar</p>
              </div>
            ) : (
              <p>No se encontraron cuentas</p>
            )}
          </div>
        ) : (
          <div className="p-4">
            {filteredCuentas.map((cuenta) => (
              <CuentaNode
                key={cuenta.id}
                cuenta={cuenta}
                onEdit={handleEdit}
                onDelete={handleDelete}
                expandedIds={expandedIds}
                toggleExpand={toggleExpand}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {modalOpen && (
        <CuentaModal
          cuenta={editingCuenta}
          parentCuenta={parentCuenta}
          onClose={() => setModalOpen(false)}
          onSave={() => { setModalOpen(false); toast.success('Cuenta guardada'); loadCuentas(); }}
        />
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        confirmText="Confirmar"
      />
    </div>
  );
}
